import { useEffect, useRef, useState, useCallback } from "react";
import { AgoraService } from "../services/AgoraService";
import { StreamService } from "../services/StreamService";
import { getRtcToken } from "../services/AgoraApiServices";
import { deriveAgoraUid } from "../utils/agoraUid";

export type LiveRoomRole = "admin" | "user";
export interface ChatMessage {
  message: string;
  sender: { userId: string; name: string; role: string };
  timestamp: string | Date;
}
export interface UseLiveRoomOptions {
  roomId?: string;
  role: LiveRoomRole;
  onConnected?: () => void;
}

function currentUser() {
  return JSON.parse(localStorage.getItem("user") || "{}");
}

export function useLiveRoom(opts: UseLiveRoomOptions) {
  const { roomId, role, onConnected } = opts;
  const agoraServiceRef = useRef<AgoraService | null>(null);
  const streamServiceRef = useRef<StreamService | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [remoteUsers, setRemoteUsers] = useState<{ uid: string }[]>([]);

  const sendMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !streamServiceRef.current) return;
    const user = currentUser();
    const userId = user._id || user.userId || user.id;
    const name = user.username || user.name || "Anonymous";
    const newMessage: ChatMessage = {
      message: trimmed,
      sender: { userId, name, role: user.role || role },
      timestamp: new Date().toISOString(),
    };
    streamServiceRef.current.socketInstance.emit("chat-message", {
      roomId,
      message: newMessage.message,
      sender: newMessage.sender,
    });
    setMessages((prev) => [...prev, newMessage]);
  }, [roomId, role]);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    const reconnectDelay = role === "admin" ? 2000 : 3000;

    const start = async () => {
      const user = currentUser();
      const userId = user._id || user.userId || user.id;
      if (!userId) throw new Error("User ID not found. Please log in again.");
      const agoraUid = deriveAgoraUid(userId);

      // --- Agora ---
      const token = await getRtcToken(roomId, agoraUid);
      if (!token) throw new Error("Failed to get valid Agora token");
      // Abort if the effect was cleaned up while awaiting (e.g. React StrictMode
      // double-mount), so we never leak a second Agora client / socket.
      if (cancelled) return;
      const agora = new AgoraService(role, token);
      agoraServiceRef.current = agora;
      await agora.initializeAgoraClient(role);
      if (cancelled) {
        agora.leaveChannel().catch(() => {});
        return;
      }
      agora.setupEventListeners();

      agora.getClient()?.on("connection-state-change", (state, reason) => {
        if (state === "CONNECTED") {
          setIsSocketConnected(true);
          onConnected?.();
        } else if (state === "DISCONNECTED" || state === "DISCONNECTING") {
          if (String(reason) !== "LEAVE" && agoraServiceRef.current &&
              !agoraServiceRef.current.isCurrentlyReconnecting()) {
            setTimeout(() => {
              agoraServiceRef.current?.forceReconnect(roomId, agoraUid, role).catch(console.error);
            }, reconnectDelay);
          }
        }
      });

      agora.getClient()?.on("user-published", (u) => {
        setRemoteUsers((prev) =>
          prev.some((x) => x.uid === u.uid.toString()) ? prev : [...prev, { uid: u.uid.toString() }]);
      });

      // --- Socket (created BEFORE the potentially-slow Agora channel join so the
      // classroom session comes up immediately; the original components never
      // blocked the socket lifecycle on the Agora join). ---
      const service = new StreamService();
      // If cleanup already ran (StrictMode double-mount), discard this socket so
      // the page doesn't end up with two sockets appending chat messages twice.
      if (cancelled) {
        service.socketInstance.disconnect();
        agora.leaveChannel().catch(() => {});
        return;
      }
      streamServiceRef.current = service;
      service.socketInstance.emit("join-room", {
        roomId, userId, role,
        name: user.username || user.name || (role === "admin" ? "Instructor" : "Student"),
      });
      service.socketInstance.on("connect", () => setIsSocketConnected(true));
      service.socketInstance.on("disconnect", () => setIsSocketConnected(false));
      service.socketInstance.on("connect_error", () => setIsSocketConnected(false));
      // If the socket connected before the listener above was attached, sync now.
      if (service.socketInstance.connected) setIsSocketConnected(true);

      // --- Agora channel join (may be slow or fail on token/cert issues). Run in
      // the background so it never blocks the socket lifecycle; failure is
      // non-fatal, matching the original components. ---
      void (async () => {
        try {
          if (role === "admin") {
            await agora.joinAsHost(roomId, agoraUid, () => { onConnected?.(); });
          } else {
            await agora.joinAsAudience(roomId, agoraUid);
            await agora.setMicrophoneMuted(true);
          }
        } catch (e) {
          console.error("Agora channel join failed (continuing with socket):", e);
        }
      })();
      service.socketInstance.on("chat-message", (data: any) => {
        const message: ChatMessage = {
          message: data.message,
          sender: {
            userId: data.sender?.userId || "anonymous",
            name: data.sender?.name || "Anonymous",
            role: data.sender?.role || "user",
          },
          timestamp: data.timestamp || new Date().toISOString(),
        };
        const me = currentUser();
        const myId = me._id || me.userId || me.id;
        if (data.sender?.userId !== myId) setMessages((prev) => [...prev, message]);
      });
    };

    start().catch((e) => { if (!cancelled) console.error("useLiveRoom init failed:", e); });

    return () => {
      cancelled = true;
      if (agoraServiceRef.current) {
        agoraServiceRef.current.turnOffCamera().catch(console.error);
        agoraServiceRef.current.turnOffMicrophone().catch(console.error);
        agoraServiceRef.current.leaveChannel().catch(console.error);
        agoraServiceRef.current = null;
      }
      if (streamServiceRef.current) {
        const user = currentUser();
        streamServiceRef.current.socketInstance.emit("leave-room", {
          roomId, userId: user._id || user.userId || user.id,
        });
        streamServiceRef.current.stopStreaming();
        streamServiceRef.current.socketInstance.disconnect();
        streamServiceRef.current = null;
      }
    };
  }, [roomId, role]); // eslint-disable-line react-hooks/exhaustive-deps

  return { agoraServiceRef, streamServiceRef, isSocketConnected, messages, setMessages, sendMessage, remoteUsers };
}
